/**
 * Picks a contact to open a conversation with. Searches the realtor's
 * contacts on the server as they type (after a short pause).
 */

import { Search } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactInitials } from "@/features/realtor/components/contact-initials";
import { useContacts } from "@/features/realtor/hooks/use-realtor-crm";

const SEARCH_DELAY_MS = 250;

export function NewConversationDialog({
	open,
	onOpenChange,
	onPick,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onPick: (contactId: string) => void;
}) {
	const { t } = useTranslation();
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[calc(100vw-2rem)] border-dashboard-border bg-dashboard-bg sm:max-w-md">
				<DialogHeader>
					<DialogTitle className="font-display italic font-medium text-dashboard-text">
						{t("realtor.messages.newConversationTitle")}
					</DialogTitle>
					<DialogDescription className="text-dashboard-muted">
						{t("realtor.messages.newConversationBody")}
					</DialogDescription>
				</DialogHeader>
				{/* Mounted only while open, so each opening starts a fresh search. */}
				{open && <ContactPicker onPick={onPick} />}
			</DialogContent>
		</Dialog>
	);
}

function ContactPicker({ onPick }: { onPick: (contactId: string) => void }) {
	const { t } = useTranslation();
	const inputId = useId();
	const [query, setQuery] = useState("");
	const [search, setSearch] = useState("");

	useEffect(() => {
		const timer = setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [query]);

	const contacts = useContacts({ q: search || undefined });
	const rows = contacts.data?.contacts ?? [];

	return (
		<div className="min-w-0 space-y-3">
			<div className="relative">
				<label htmlFor={inputId} className="sr-only">
					{t("realtor.messages.searchLabel")}
				</label>
				<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dashboard-muted" />
				<input
					id={inputId}
					type="search"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					placeholder={t("realtor.messages.searchPlaceholder")}
					className="h-10 w-full rounded-lg border border-dashboard-border bg-dashboard-bg pl-9 pr-3 text-sm text-dashboard-text placeholder:text-dashboard-muted focus:outline-none focus:ring-2 focus:ring-dashboard-accent/40"
				/>
			</div>

			{contacts.isPending && (
				<div className="space-y-2" data-testid="messages-contacts-loading">
					<Skeleton className="h-11 w-full bg-dashboard-border" />
					<Skeleton className="h-11 w-full bg-dashboard-border" />
				</div>
			)}

			{contacts.error && (
				<p className="text-sm text-dashboard-muted" role="alert">
					{contacts.error.message || t("realtor.messages.contactsLoadFailed")}
				</p>
			)}

			{contacts.data && !contacts.isPlaceholderData && rows.length === 0 && (
				<div className="space-y-2 py-2 text-sm text-dashboard-muted">
					<p>
						{search
							? t("realtor.messages.noContactMatches", { q: search })
							: t("realtor.messages.noContacts")}
					</p>
					{!search && (
						<Link
							to="/realtor/contacts"
							className="font-medium text-dashboard-accent-dark hover:underline"
						>
							{t("realtor.messages.goToContacts")}
						</Link>
					)}
				</div>
			)}

			{rows.length > 0 && (
				<ul className="max-h-72 space-y-1 overflow-y-auto">
					{rows.map(({ contact }) => (
						<li key={contact.id}>
							<button
								type="button"
								onClick={() => onPick(contact.id)}
								className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-dashboard-surface"
							>
								<Avatar className="h-8 w-8 shrink-0 border border-dashboard-border">
									<AvatarFallback className="bg-dashboard-border text-[11px] font-semibold text-dashboard-text">
										<ContactInitials name={contact.name} />
									</AvatarFallback>
								</Avatar>
								<span className="min-w-0 flex-1">
									<span className="block truncate text-sm font-medium text-dashboard-text">
										{contact.name}
									</span>
									<span className="block truncate text-[11px] text-dashboard-muted">
										{t(`realtor.messages.contactTypes.${contact.type}`)}
										{contact.city ? ` · ${contact.city}` : ""}
									</span>
								</span>
							</button>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}
